import { useState, useEffect, useCallback } from 'react';
import { X, ExternalLink, Wand2, Maximize2, ChevronLeft, ChevronRight } from 'lucide-react';
import CopyButton from './CopyButton';

export default function LibraryDetailModal({ inspiration, isDark, onClose }) {
  const [mounted, setMounted] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const images = inspiration.images || [];
  const activeImage = images[activeImageIndex];

  const showPrevImage = useCallback(() => {
    setActiveImageIndex((i) => (i - 1 + images.length) % images.length);
  }, [images.length]);

  const showNextImage = useCallback(() => {
    setActiveImageIndex((i) => (i + 1) % images.length);
  }, [images.length]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const handleClose = useCallback(() => {
    setMounted(false);
    setTimeout(onClose, 180);
  }, [onClose]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') {
        if (lightboxOpen) {
          setLightboxOpen(false);
        } else {
          handleClose();
        }
      } else if (lightboxOpen && images.length > 1) {
        if (e.key === 'ArrowLeft') showPrevImage();
        if (e.key === 'ArrowRight') showNextImage();
      }
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = 'unset';
    };
  }, [handleClose, lightboxOpen, images.length, showPrevImage, showNextImage]);

  const allTags = [
    ...(inspiration.design_types || []),
    ...(inspiration.niches || []),
  ];

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-base ease-smooth ${
        mounted ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        onClick={(e) => e.stopPropagation()}
        className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border transition-all duration-base ease-smooth ${
          mounted ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        } ${isDark ? 'bg-background-dark border-border-dark' : 'bg-white border-border'}`}
      >
        {/* Image */}
        {images.length > 0 && (
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            className={`group/img relative block w-full aspect-[16/10] overflow-hidden ${isDark ? 'bg-border-dark' : 'bg-background-sidebar'}`}
          >
            <img src={activeImage.url} alt={inspiration.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover/img:bg-black/20 transition-colors duration-fast flex items-center justify-center">
              <span
                className={`opacity-0 group-hover/img:opacity-100 transition-opacity duration-fast p-2.5 rounded-full bg-black/60 text-white`}
              >
                <Maximize2 size={18} />
              </span>
            </div>
          </button>
        )}

        {images.length > 1 && (
          <div className="flex gap-2 p-3 overflow-x-auto">
            {images.map((img, i) => (
              <button
                key={img.url}
                type="button"
                onClick={() => setActiveImageIndex(i)}
                title={img.brief || (img.backgroundRecipe ? 'Has a background recipe' : undefined)}
                className={`relative shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-colors duration-fast ${
                  i === activeImageIndex
                    ? isDark
                      ? 'border-primary-dark'
                      : 'border-primary'
                    : 'border-transparent opacity-60 hover:opacity-100'
                }`}
              >
                <img src={img.url} alt={`${inspiration.title} ${i + 1}`} className="w-full h-full object-cover" />
                {(img.brief || img.backgroundRecipe) && (
                  <span
                    className={`absolute top-1 right-1 w-2 h-2 rounded-full ring-2 ${
                      isDark ? 'bg-amber-500 ring-background-dark' : 'bg-amber-500 ring-white'
                    }`}
                  />
                )}
              </button>
            ))}
          </div>
        )}

        <div className="p-6">
          {/* Title + Close */}
          <div className="flex items-start justify-between gap-4 mb-1">
            <h2 className={`text-2xl font-display font-semibold ${isDark ? 'text-white' : 'text-text'}`}>
              {inspiration.title}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className={`shrink-0 p-1.5 rounded-lg transition-all duration-fast active:scale-90 ${
                isDark ? 'text-gray-400 hover:text-white hover:bg-border-dark' : 'text-text-muted hover:text-text hover:bg-background-sidebar'
              }`}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {inspiration.brief && (
            <p className={`text-sm italic leading-relaxed mb-4 ${isDark ? 'text-gray-400' : 'text-text-muted'}`}>
              {inspiration.brief}
            </p>
          )}

          {/* Tags */}
          {allTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-4">
              {allTags.map((tag) => (
                <span
                  key={tag}
                  className={`text-xs px-2.5 py-1 rounded-full ${
                    isDark ? 'bg-border-dark text-gray-300' : 'bg-background-sidebar text-text-muted'
                  }`}
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Category + Source */}
          <div className="flex items-center gap-4 mb-4 text-xs">
            {inspiration.category && (
              <span
                className={`px-2 py-1 rounded font-medium ${
                  isDark ? 'bg-primary-dark/20 text-primary-dark' : 'bg-primary/10 text-primary'
                }`}
              >
                {inspiration.category}
              </span>
            )}
            {inspiration.source?.startsWith('http') ? (
              <a
                href={inspiration.source}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1 transition-colors duration-fast ${
                  isDark ? 'text-primary-dark hover:text-primary-dark/80' : 'text-primary hover:text-primary-hover'
                }`}
              >
                View Source
                <ExternalLink size={12} />
              </a>
            ) : inspiration.source ? (
              <span className={isDark ? 'text-gray-500' : 'text-text-light'}>{inspiration.source}</span>
            ) : null}
          </div>

          {/* Animation */}
          {inspiration.has_animation && (
            <div className={`mb-4 p-3 rounded-lg border ${isDark ? 'bg-accent-dark/10 border-accent-dark/30' : 'bg-accent/10 border-accent/30'}`}>
              <p className={`text-xs font-medium flex items-center gap-1.5 mb-1 ${isDark ? 'text-accent-dark' : 'text-accent'}`}>
                <Wand2 size={13} />
                Animation
              </p>
              {inspiration.animation_notes && (
                <p className={`text-xs leading-relaxed ${isDark ? 'text-gray-400' : 'text-text-muted'}`}>
                  {inspiration.animation_notes}
                </p>
              )}
            </div>
          )}

          {/* Per-image notes - amber-highlighted when it carries a background recipe */}
          {(activeImage?.brief || activeImage?.backgroundRecipe) && (
            <div
              className={`mb-4 p-3 rounded-lg border ${
                activeImage.backgroundRecipe
                  ? isDark
                    ? 'bg-amber-900/10 border-amber-800/40'
                    : 'bg-amber-50 border-amber-200'
                  : isDark
                    ? 'bg-background-dark border-border-dark'
                    : 'bg-background-sidebar border-border/50'
              }`}
            >
              <p
                className={`text-xs font-medium uppercase tracking-wide mb-1.5 ${
                  activeImage.backgroundRecipe
                    ? isDark
                      ? 'text-amber-500'
                      : 'text-amber-700'
                    : isDark
                      ? 'text-gray-400'
                      : 'text-text-muted'
                }`}
              >
                {activeImage.backgroundRecipe ? 'Image Recipe' : 'Image Note'} · Image {activeImageIndex + 1} of {images.length}
              </p>
              {activeImage.backgroundRecipe && (
                <p className={`text-xs font-mono leading-relaxed ${isDark ? 'text-amber-200/80' : 'text-amber-900/80'}`}>
                  {activeImage.backgroundRecipe}
                </p>
              )}
              {activeImage.brief && (
                <p className={`text-xs leading-relaxed ${activeImage.backgroundRecipe ? 'mt-2' : ''} ${isDark ? 'text-gray-400' : 'text-text-muted'}`}>
                  {activeImage.brief}
                </p>
              )}
            </div>
          )}

          {/* Actions */}
          <div className={`flex items-center gap-2 pt-4 border-t ${isDark ? 'border-border-dark' : 'border-border/50'}`}>
            {inspiration.brief && <CopyButton text={inspiration.brief} isDark={isDark} label="Copy Brief" variant="solid" />}
            {activeImage?.backgroundRecipe && (
              <CopyButton text={activeImage.backgroundRecipe} isDark={isDark} label="Copy Recipe" />
            )}
            <button
              type="button"
              onClick={handleClose}
              className={`ml-auto px-3 py-2 rounded-lg text-xs font-medium transition-all duration-fast active:scale-95 ${
                isDark ? 'text-gray-400 hover:text-white' : 'text-text-muted hover:text-text'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {lightboxOpen && (
        <div
          onClick={() => setLightboxOpen(false)}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-6"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxOpen(false);
            }}
            className="absolute top-6 right-6 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all duration-fast active:scale-90"
            aria-label="Close full image"
          >
            <X size={22} />
          </button>

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  showPrevImage();
                }}
                className="absolute left-4 sm:left-6 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all duration-fast active:scale-90"
                aria-label="Previous image"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  showNextImage();
                }}
                className="absolute right-4 sm:right-6 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-all duration-fast active:scale-90"
                aria-label="Next image"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          <img
            src={activeImage.url}
            alt={inspiration.title}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-lg"
          />

          {images.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-xs font-mono">
              {activeImageIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
